
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fetchStorageLocations } from "./inventoryService";
import { StorageLocation } from "./inventoryTypes";
import { Plus, Edit, Trash2, Search } from "lucide-react";
import { StorageLocationDialog } from "./StorageLocationDialog";

export function StorageLocationList() {
  const { data: locations = [], isLoading, error, refetch } = useQuery({
    queryKey: ["storageLocations"],
    queryFn: fetchStorageLocations,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLocation, setSelectedLocation] = useState<StorageLocation | null>(null);
  const [showNewLocationDialog, setShowNewLocationDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);

  const filteredLocations = locations.filter((location) =>
    location.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleNewLocationClick = () => {
    setSelectedLocation(null);
    setShowNewLocationDialog(true);
  };

  const handleEditClick = (location: StorageLocation, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedLocation(location);
    setShowNewLocationDialog(true);
  };

  const handleDialogClose = (updated: boolean) => {
    setShowNewLocationDialog(false);
    setSelectedLocation(null);
    if (updated) {
      refetch();
    }
  };

  if (isLoading) return <div className="p-8 text-center">Loading storage locations...</div>;

  if (error) return <div className="p-8 text-center text-red-500">Error loading storage locations</div>;

  return (
    <div className="space-y-4">
      {/* Filters and actions */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search locations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
        
        <Button onClick={handleNewLocationClick}>
          <Plus className="mr-2 h-4 w-4" />
          Add Location
        </Button>
      </div>

      {/* Locations Table */}
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="w-[100px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredLocations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} className="text-center py-4">
                  No storage locations found
                </TableCell>
              </TableRow>
            ) : (
              filteredLocations.map((location) => (
                <TableRow key={location.id}>
                  <TableCell className="font-medium">{location.name}</TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={(e) => handleEditClick(location, e)}
                      className="h-8 w-8 p-0 mr-2"
                    >
                      <Edit className="h-4 w-4" />
                      <span className="sr-only">Edit</span>
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dialogs */}
      {showNewLocationDialog && (
        <StorageLocationDialog 
          location={selectedLocation} 
          open={showNewLocationDialog} 
          onClose={handleDialogClose} 
        />
      )}
    </div>
  );
}
